// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {Logger} from '@app/features/platform/utils/AppLogger';

const logger = new Logger('RemoteDeepFilterNode');

const DEEP_FILTER_PROCESSOR_NAME = 'fluxer-deep-filter';
const DEEP_FILTER_SAMPLE_RATE = 48000;
const DEEP_FILTER_ATTEN_LIM_DB = 30;
const DEEP_FILTER_INPUT_GAIN = 10;
const DEEP_FILTER_OUTPUT_GAIN = 0.1;
const DEEP_FILTER_HIGH_PASS_HZ = 60;
const DEEP_FILTER_ASSET_TOTAL_TIMEOUT_MS = 5 * 60_000;
const DEEP_FILTER_STARTUP_TIMEOUT_MS = 10_000;
const GZIP_MAGIC = [0x1f, 0x8b] as const;

interface DeepFilterAssets {
	wasmModule: WebAssembly.Module;
	modelBytes: ArrayBuffer;
}

export interface RemoteDeepFilterNode {
	node: AudioWorkletNode;
	dispose: () => Promise<void>;
}

let assetsPromise: Promise<DeepFilterAssets> | null = null;

function deepFilterAssetUrl(file: string): string {
	return `${RuntimeConfig.staticCdnEndpoint}/libs/deepfilternet3/v3/${file}`;
}

async function loadAssets(signal: AbortSignal): Promise<DeepFilterAssets> {
	const [wasmBytes, modelBytes] = await Promise.all([
		fetch(deepFilterAssetUrl('pkg/df_bg.wasm'), {credentials: 'same-origin', signal}).then(
			async (response) => {
				if (!response.ok) {
					throw new Error(`DeepFilter wasm request failed with status ${response.status}`);
				}
				return response.arrayBuffer();
			},
		),
		fetch(deepFilterAssetUrl('models/DeepFilterNet3_onnx.tar.gz'), {
			credentials: 'same-origin',
			signal,
		}).then(async (response) => {
			if (!response.ok) {
				throw new Error(`DeepFilter model request failed with status ${response.status}`);
			}
			return response.arrayBuffer();
		}),
	]);
	signal.throwIfAborted();
	const header = new Uint8Array(modelBytes, 0, Math.min(GZIP_MAGIC.length, modelBytes.byteLength));
	if (header.length < GZIP_MAGIC.length || header[0] !== GZIP_MAGIC[0] || header[1] !== GZIP_MAGIC[1]) {
		throw new Error('DeepFilter model asset is not a gzip archive');
	}
	const wasmModule = await WebAssembly.compile(wasmBytes);
	signal.throwIfAborted();
	return {wasmModule, modelBytes};
}

export function loadRemoteDeepFilterAssets(): Promise<DeepFilterAssets> {
	if (!assetsPromise) {
		const controller = new AbortController();
		const timeoutId = window.setTimeout(() => {
			controller.abort(new Error('DeepFilter assets took too long to download'));
		}, DEEP_FILTER_ASSET_TOTAL_TIMEOUT_MS);
		const load = loadAssets(controller.signal)
			.catch((error: unknown) => {
				if (assetsPromise === load) assetsPromise = null;
				throw error;
			})
			.finally(() => {
				window.clearTimeout(timeoutId);
			});
		assetsPromise = load;
	}
	return assetsPromise;
}

export function prefetchRemoteDeepFilterAssets(): void {
	void loadRemoteDeepFilterAssets().catch(() => undefined);
}

const workletModuleLoads = new WeakMap<BaseAudioContext, Promise<void>>();

function ensureDeepFilterWorkletModule(context: BaseAudioContext): Promise<void> {
	const existing = workletModuleLoads.get(context);
	if (existing) return existing;
	const load = import('@app/features/voice/utils/noise_suppression/NoiseSuppressionWorkletAssets').then(
		(assets) => context.audioWorklet.addModule(assets.DEEP_FILTER_WORKLET_URL),
	);
	workletModuleLoads.set(context, load);
	void load.catch(() => {
		if (workletModuleLoads.get(context) === load) workletModuleLoads.delete(context);
	});
	return load;
}

interface DeepFilterWorkletSignal {
	type?: unknown;
	message?: unknown;
}

function waitForDeepFilterReady(node: AudioWorkletNode, signal: AbortSignal): Promise<void> {
	return new Promise((resolve, reject) => {
		let settled = false;
		const finish = (error?: unknown) => {
			if (settled) return;
			settled = true;
			node.port.onmessage = null;
			node.onprocessorerror = null;
			signal.removeEventListener('abort', onAbort);
			if (error !== undefined) reject(error);
			else resolve();
		};
		const onAbort = () => finish(signal.reason);
		if (signal.aborted) {
			onAbort();
			return;
		}
		signal.addEventListener('abort', onAbort, {once: true});
		node.port.onmessage = (event: MessageEvent) => {
			const data = event.data as DeepFilterWorkletSignal | null;
			if (!data || typeof data !== 'object') return;
			if (data.type === 'ready') {
				finish();
				return;
			}
			if (data.type === 'error') {
				finish(
					new Error(
						`DeepFilter worklet failed: ${typeof data.message === 'string' ? data.message : 'unknown error'}`,
					),
				);
			}
		};
		node.onprocessorerror = () => finish(new Error('DeepFilter worklet raised a processor error'));
		node.port.start();
	});
}

export async function createRemoteDeepFilterNode(audioContext: AudioContext): Promise<RemoteDeepFilterNode> {
	if (audioContext.sampleRate !== DEEP_FILTER_SAMPLE_RATE) {
		throw new Error(
			`DeepFilter requires a ${DEEP_FILTER_SAMPLE_RATE} Hz context (got ${audioContext.sampleRate} Hz)`,
		);
	}
	const controller = new AbortController();
	const timeoutId = window.setTimeout(() => {
		controller.abort(new Error('DeepFilter worklet startup timed out'));
	}, DEEP_FILTER_STARTUP_TIMEOUT_MS);
	let node: AudioWorkletNode | null = null;
	try {
		const [assets] = await Promise.all([
			loadRemoteDeepFilterAssets(),
			ensureDeepFilterWorkletModule(audioContext),
		]);
		controller.signal.throwIfAborted();
		node = new AudioWorkletNode(audioContext, DEEP_FILTER_PROCESSOR_NAME, {
			numberOfInputs: 1,
			numberOfOutputs: 1,
			outputChannelCount: [1],
			channelCount: 1,
			channelCountMode: 'explicit',
			channelInterpretation: 'speakers',
			processorOptions: {
				wasmModule: assets.wasmModule,
				modelBytes: assets.modelBytes,
				attenLimDb: DEEP_FILTER_ATTEN_LIM_DB,
				inputGain: DEEP_FILTER_INPUT_GAIN,
				outputGain: DEEP_FILTER_OUTPUT_GAIN,
				highPassHz: DEEP_FILTER_HIGH_PASS_HZ,
			},
		});
		await waitForDeepFilterReady(node, controller.signal);
		controller.signal.throwIfAborted();
		const readyNode = node;
		readyNode.onprocessorerror = () => {
			logger.warn('DeepFilter worklet raised a processor error');
		};
		let disposal: Promise<void> | null = null;
		return {
			node: readyNode,
			dispose: () => {
				disposal ??= (async () => {
					readyNode.port.onmessage = null;
					readyNode.onprocessorerror = null;
					try {
						readyNode.port.postMessage({type: 'dispose'});
						readyNode.port.close();
					} catch (error) {
						logger.debug('Failed to close DeepFilter worklet port', error);
					}
					try {
						readyNode.disconnect();
					} catch {}
				})();
				return disposal;
			},
		};
	} catch (error) {
		controller.abort(error);
		if (node) {
			try {
				node.port.close();
			} catch {}
			try {
				node.disconnect();
			} catch {}
		}
		throw error;
	} finally {
		window.clearTimeout(timeoutId);
	}
}
